<template>
  <div class="target-price-forecast">
    <MoreTitle title="目标价预测" />
    <div class="chart-box edge-padding">
      <VueEcharts :option="option" />
      <div class="line-box" v-if="goalpriceData.lowPrice">
        <div
          class="line"
          v-for="item in [...legendMap].reverse()"
          :key="item.alias"
          :style="{
            transform: `scaleX(${item.pre / goalpriceData.lowPrice})`,
            backgroundColor: `${item.color}`
          }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VueEcharts from '@/components/common/VueEcharts.vue'
import { i18nTranslateInit } from '@/utils/utility-lang'
import { postcssPatch } from '@/utils/postcssPatch'
import theme from '@/theme'
export default {
  components: { VueEcharts },
  inject: ['theme'],
  data() {
    return {
      goalpriceData: {},
      klineList: [],
      useOpenCC: i18nTranslateInit.useOpenCC
    }
  },
  computed: {
    symbol() {
      return this.$route.query.symbol
    },
    market() {
      return this.$funs.getMarket(this.symbol)
    },
    maxmin() {
      const highList = this.klineList.map((item) => item.high)
      const lowList = this.klineList.map((item) => item.low)
      const max = Math.max(...highList)
      const min = Math.min(...lowList)
      const ave = (max + min) / 2
      return { max, min, ave }
    },
    legendMap() {
      const { highPrice, lowPrice, avePrice } = this.goalpriceData
      return [
        { name: this.useOpenCC('预测最低价'), alias: this.useOpenCC('最低'), pre: lowPrice, color: '#32C77F' },
        { name: this.useOpenCC('目标均价'), alias: this.useOpenCC('均价'), pre: avePrice, color: '#3881F7' },
        { name: this.useOpenCC('预测最高价'), alias: this.useOpenCC('最高'), pre: highPrice, color: '#FF4C4C' }
      ]
    },
    option() {
      const { max } = this.maxmin
      const { highPrice, lowPrice } = this.goalpriceData
      const klineList = this.klineList
      const firstItem = klineList[0] ?? {}
      return {
        grid: [
          {
            top: postcssPatch(50),
            bottom: postcssPatch(20),
            left: 0,
            width: postcssPatch(70)
          },
          {
            top: postcssPatch(50),
            bottom: postcssPatch(20),
            left: postcssPatch(70),
            right: 0
          }
        ],
        title: {
          subtext: this.useOpenCC(
            `${this.$t('单位')}：${
              this.$t({ CNY: '人民币', USD: '美元', HKD: '港元' }?.[this.goalpriceData?.currency]) ?? '--'
            }`
          ),
          top: postcssPatch(-10),
          right: 0,
          textStyle: {
            color: this.theme['--font-color-5']
          }
        },
        legend: {
          itemHeight: postcssPatch(8),
          itemWidth: postcssPatch(8),
          icon: 'rect',
          top: 0,
          left: 0,
          textStyle: {
            color: this.theme['--font-color-5']
          },
          data: this.legendMap.map(({ name, color }) => {
            return {
              name,
              itemStyle: { color }
            }
          })
        },
        xAxis: [
          {
            type: 'category',
            data: ['-'], // x轴空间占位
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: 'transparent'
            }
          },
          {
            type: 'category',
            gridIndex: 1,
            inverse: true,
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: this.theme['--font-color-5'],
              interval: 0,
              width: postcssPatch(60),
              formatter: (value, index) => {
                const date = this.$dayjs(value).format('MM-DD')
                if (index === 0) {
                  return `{left|${date}}`
                } else if (index === 15) {
                  return date
                } else if (index === 29) {
                  return `{right|${date}}`
                }
              },
              rich: {
                left: {
                  align: 'left'
                },
                right: {
                  align: 'right'
                }
              }
            }
          }
        ],
        yAxis: [
          {
            max: highPrice,
            min: lowPrice,
            interval: (highPrice - lowPrice) / 2,
            axisLabel: {
              inside: true,
              margin: 0,
              color: this.theme['--font-color-5'],
              verticalAlign: 'bottom',
              formatter: (value, index) => {
                const { alias, pre } = this.legendMap[index] ?? {}
                return alias + this.$digitjs(pre).rounded(3)
              },
              lineHeight: postcssPatch(24)
            },
            splitLine: {
              lineStyle: {
                color: this.theme['--border-color-5'],
                width: postcssPatch(2)
              }
            }
          },
          {
            gridIndex: 1,
            max: highPrice,
            min: lowPrice,
            interval: (highPrice - lowPrice) / 2,
            axisLabel: {
              show: false
            },
            splitLine: {
              lineStyle: {
                color: this.theme['--border-color-5'],
                width: postcssPatch(2)
              }
            }
          }
        ],
        dataset: {
          source: klineList
        },
        series: [
          ...this.legendMap.map(({ name }) => {
            return {
              name,
              type: 'scatter',
              data: []
            }
          }),
          {
            type: 'candlestick',
            xAxisIndex: 1,
            yAxisIndex: 1,
            encode: {
              x: 'date',
              y: ['open', 'close', 'low', 'high']
            },
            itemStyle: {
              color: theme['--up-color'], // 阳线颜色
              color0: theme['--down-color'], // 阴线颜色
              borderColor: theme['--up-color'], // 阳线边框颜色
              borderColor0: theme['--down-color'] // 阴线边框颜色
            },
            markPoint: {
              symbol: 'rect',
              symbolSize: 1,
              itemStyle: {
                color: 'transparent'
              },
              label: {
                position: 'left',
                color: this.theme['--font-color-5']
              },
              data: [
                {
                  coord: [firstItem.date, max],
                  value: this.$t('昨收') + firstItem.preClose ?? 0
                }
              ]
            }
          }
        ]
      }
    }
  },
  created() {
    this.getGoalprice()
    this.getKlineList()
  },
  methods: {
    async getGoalprice() {
      const res = await this.$post(this.$api.f10[this.market] + '/summary/forecast-goalprice ', {
        symbol: this.symbol
      })
      this.goalpriceData = res.data[0] ?? []
    },
    async getKlineList() {
      let data

      if (['hk', 'us'].includes(this.market)) {
        const res = await this.$post(
          this.$api.sdk[this.market] + '/chart/kline/list',
          {
            candleMode: 0,
            date: this.$dayjs().format('YYYY-MM-DD'),
            number: 30,
            symbol: this.symbol,
            orderMode: 0,
            timeMode: 0,
            type: 0
          },
          {
            headers: {
              'Org-Data-Enable': 'false'
            }
          }
        )

        data = res.data.records
      } else {
        const res = await this.$post(this.$api.sdk[this.market] + '/chart/kline/list', {
          candleMode: 0,
          date: this.$dayjs().format('YYYY-MM-DD'),
          pageNum: 1,
          pageSize: 30,
          symbol: this.symbol,
          timeMode: 0,
          type: 0
        })
        data = res.body?.list
      }

      this.klineList = data ?? []
    }
  }
}
</script>

<style lang="scss" scoped>
.target-price-forecast {
  background: var(--bg-color-2);

  .chart-box {
    position: relative;

    .line-box {
      position: absolute;
      top: calc(49px + var(--edge-padding));
      left: var(--edge-padding);
      width: 345px;
      height: 102px;
      display: flex;
      flex-direction: column;
      align-items: start;
      justify-content: space-between;

      .line {
        width: 70px;
        height: 2px;
        transform-origin: left center;
        background: var(--border-color-5);
      }
    }
  }
}
</style>
